class EPFreeGiftManager {
  constructor() {
    this.PROPERTY_FLAG = '_free_gift';
    this.PROPERTY_MILESTONE = '_fg_milestone';
    this.isUpdating = false;
    this.pendingSync = false;

    this.bindEvents();
    this.scheduleSync();
  }

  bindEvents() {
    document.addEventListener('on:cart:add', () => this.scheduleSync());
    document.addEventListener('on:cart:after-merge', () => this.scheduleSync());
    document.addEventListener('on:cart-drawer:after-open', () => this.scheduleSync());
  }

  scheduleSync() {
    if (this.pendingSync) return;
    this.pendingSync = true;
    setTimeout(() => {
      this.pendingSync = false;
      this.syncGifts();
    }, 150);
  }

  async getCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return res.json();
  }

  async syncGifts() {
    if (this.isUpdating) return;

    const blocks = document.querySelectorAll('[data-ep-fg]');
    if (!blocks.length) return;

    this.isUpdating = true;

    try {
      const cart = await this.getCart();
      const cartNoGifts = this.cartTotalsWithoutGifts(cart);

      const toAdd = [];
      const toRemove = [];

      blocks.forEach((block) => {
        const type = block.dataset.fgType;
        const milestone = parseInt(block.dataset.fgMilestone, 10);
        const variantId = parseInt(block.dataset.fgVariantId, 10);

        const reached =
          type === 'qty'
            ? cartNoGifts.item_count >= milestone
            : cartNoGifts.total_price >= milestone;

        const existingLine = this.findGiftLine(cart, variantId, milestone);

        if (reached && !existingLine) {
          toAdd.push({ variantId, milestone });
        } else if (!reached && existingLine) {
          toRemove.push(existingLine.key);
        }

        block.classList.toggle('reached', reached);

        this.updateHeadline(block, type, milestone, cartNoGifts);
      });

      let cartChanged = false;

      for (const key of toRemove) {
        await this.removeLine(key);
        cartChanged = true;
      }

      if (toAdd.length) {
        await this.addGifts(toAdd);
        cartChanged = true;
      }

      if (cartChanged) {
        document.dispatchEvent(
          new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true })
        );
      }
    } catch (err) {
      console.error('[EPFreeGift] sync error:', err);
    } finally {
      this.isUpdating = false;
    }
  }

  updateHeadline(block, type, milestone, cartNoGifts) {
    const headlineEl = block.querySelector('[data-fg-headline]');
    if (!headlineEl) return;

    const template = block.dataset.fgHeadlineTemplate || '';
    if (!template.includes('|x|')) return;

    let remainingDisplay;

    if (type === 'qty') {
      const remaining = Math.max(0, milestone - cartNoGifts.item_count);
      remainingDisplay = String(remaining);
    } else {
      const remaining = Math.max(0, milestone - cartNoGifts.total_price);
      remainingDisplay = this.formatMoney(remaining);
    }

    headlineEl.textContent = template.replace('|x|', remainingDisplay);
  }

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

    const amount = cents / 100;
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  cartTotalsWithoutGifts(cart) {
    let item_count = 0;
    let total_price = 0;
    cart.items.forEach((item) => {
      const isGift = item.properties && item.properties[this.PROPERTY_FLAG] === 'true';
      if (!isGift) {
        item_count += item.quantity;
        total_price += item.final_line_price;
      }
    });
    return { item_count, total_price };
  }

  findGiftLine(cart, variantId, milestone) {
    return cart.items.find(
      (item) =>
        item.variant_id === variantId &&
        item.properties &&
        item.properties[this.PROPERTY_FLAG] === 'true' &&
        String(item.properties[this.PROPERTY_MILESTONE]) === String(milestone)
    );
  }

  async addGifts(gifts) {
    const items = gifts.map(({ variantId, milestone }) => ({
      id: variantId,
      quantity: 1,
      properties: {
        [this.PROPERTY_FLAG]: 'true',
        [this.PROPERTY_MILESTONE]: String(milestone),
      },
    }));

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items }),
    });
  }

  async removeLine(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new EPFreeGiftManager());
} else {
  new EPFreeGiftManager();
}